import { apiClient } from '@shared/api';
import { tokenService } from './tokenService';
import type { ApiResponse } from '@shared/types';
import type {
  AuthenticationDataDto,
  UserInfoDto,
  UserProfileDto,
  LoginRequest,
  RegisterRequest,
  RegistrationResultDto,
  ChangePasswordRequest,
  RevokeTokenRequest,
} from '../types';

class AuthService {
  async login(credentials: LoginRequest): Promise<AuthenticationDataDto> {
    const { data } = await apiClient.post<ApiResponse<AuthenticationDataDto>>(
      '/authentication/login',
      credentials
    );

    if (data.success && data.data) {
      tokenService.setTokens(data.data);
      return data.data;
    }

    throw new Error(data.message || 'Login failed');
  }

  async register(request: RegisterRequest): Promise<RegistrationResultDto> {
    const { data } = await apiClient.post<ApiResponse<RegistrationResultDto>>(
      '/authentication/register',
      request
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Registration failed');
  }

  async logout(): Promise<void> {
    const refreshToken = tokenService.getRefreshToken();
    if (refreshToken) {
      try {
        // skipAuthRefresh (86d3zp17w): this body carries a refresh token captured before dispatch.
        // If the interceptor refreshed and rotated mid-flight, the retried logout would revoke the
        // PARENT and leave the live successor valid for its full lifetime.
        await apiClient.post(
          '/authentication/logout',
          { refreshToken },
          { skipAuthRefresh: true }
        );
      } catch {
        // Ignore logout errors - end the session anyway
      }
    }
    // endSession, not clearTokens: this is a deliberate end, so siblings must be told (86d3zp17w).
    tokenService.endSession('user-logout');
  }

  async getCurrentUser(): Promise<UserInfoDto> {
    const { data } = await apiClient.get<ApiResponse<UserInfoDto>>(
      '/authentication/me'
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to get user info');
  }

  async getProfile(): Promise<UserProfileDto> {
    const { data } = await apiClient.get<ApiResponse<UserProfileDto>>(
      '/authentication/profile'
    );

    if (data.success && data.data) {
      return data.data;
    }

    throw new Error(data.message || 'Failed to get profile');
  }

  async changePassword(request: ChangePasswordRequest): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<object>>(
      '/authentication/change-password',
      request
    );

    if (!data.success) {
      throw new Error(data.message || 'Failed to change password');
    }
  }

  async revokeToken(request: RevokeTokenRequest): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<object>>(
      '/authentication/admin/revoke-token',
      request
    );

    if (!data.success) {
      throw new Error(data.message || 'Failed to revoke token');
    }
  }

  async revokeAllUserTokens(request: RevokeTokenRequest): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<object>>(
      '/authentication/admin/revoke-all-tokens',
      request
    );

    if (!data.success) {
      throw new Error(data.message || 'Failed to revoke tokens');
    }
  }

  isAuthenticated(): boolean {
    return !!tokenService.getAccessToken() && !tokenService.isTokenExpired();
  }
}

export const authService = new AuthService();
